import { useEffect, useState } from "react";
import ReactGA from "react-ga4";
import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";

const API_BASE = (process.env.REACT_APP_SERVER_BASE_URL || "http://localhost:3001").replace(/\/$/, "");

const REASONS = [
  { value: "chain_franchise", label: "This is a chain or franchise" },
  { value: "not_coffee_shop", label: "This isn't a coffee shop" },
  { value: "permanently_closed", label: "Permanently closed" },
  { value: "other", label: "Other" },
];

/**
 * Flag / report modal — shared by shop profile and Explore list cards.
 * Opens with isOpen; owns reason + submit state; resets on close.
 */
const ReportShopModal = ({ isOpen, onClose, shopId, shopName }) => {
  const surface = useColorModeValue("#fdfaf5", "#1e1510");
  const cream = useColorModeValue("#1a0c04", "#e8d9c4");
  const creamDim = useColorModeValue("#6a4020", "#a89880");
  const border = useColorModeValue("rgba(0,0,0,0.10)", "rgba(255,255,255,0.07)");
  const accent = useColorModeValue("#8b3e12", "#c8a97e");
  const errorColor = useColorModeValue("red.600", "red.300");

  const [reason, setReason] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setReason(null);
      setLoading(false);
      setDone(false);
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!reason || !shopId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/shops/${shopId}/report`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason }),
      });
      if (res.status === 409) {
        setError("You've already flagged this shop.");
      } else if (res.status === 429) {
        setError("Too many reports submitted. Please try again later.");
      } else if (!res.ok) {
        setError("Something went wrong. Please try again.");
      } else {
        ReactGA.event({
          category: "Shop",
          action: "Report",
          label: `${shopName || shopId} — ${reason}`,
        });
        setDone(true);
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="sm">
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent bg={surface} color={cream} borderRadius="20px" border={`1px solid ${border}`} mx={4}>
        <ModalHeader fontSize="18px" fontWeight={400} pb={1}>
          Flag this shop
        </ModalHeader>
        <ModalCloseButton color={creamDim} />
        {done ? (
          <ModalBody pb={6}>
            <Text fontSize="13px" color={creamDim}>Thanks - we'll review it.</Text>
          </ModalBody>
        ) : error ? (
          <ModalBody pb={6}>
            <Text fontSize="13px" color={errorColor}>{error}</Text>
          </ModalBody>
        ) : (
          <>
            <ModalBody>
              <Text fontSize="12px" color={creamDim} mb={4}>What's the issue?</Text>
              <Stack spacing={2}>
                {REASONS.map(({ value, label }) => (
                  <Box
                    key={value}
                    px={3}
                    py="10px"
                    borderRadius="10px"
                    border={`1px solid ${reason === value ? accent : border}`}
                    bg={reason === value ? `${accent}18` : "transparent"}
                    cursor="pointer"
                    fontSize="13px"
                    color={reason === value ? accent : creamDim}
                    onClick={() => setReason(value)}
                  >
                    {label}
                  </Box>
                ))}
              </Stack>
            </ModalBody>
            <ModalFooter gap={2}>
              <Button size="sm" variant="ghost" color={creamDim} onClick={onClose}>Cancel</Button>
              <Button
                size="sm"
                bg={accent}
                color="white"
                borderRadius="full"
                isDisabled={!reason}
                isLoading={loading}
                _hover={{ opacity: 0.85 }}
                onClick={handleSubmit}
              >
                Submit
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default ReportShopModal;
